/** @jsx jsx */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { type ReactNode, useCallback } from 'react';
import { css, jsx } from '@compiled/react';

import Link from '@atlaskit/link';
import { IconButton } from '@atlaskit/button/new';
import type { NewCoreIconProps } from '@atlaskit/icon/base-new';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import { Text, Flex } from '@atlaskit/primitives';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import {
	AdControlDropdownMenu,
	DismissalButton,
	ReasonsButton,
} from '@atlassian/ad-control-toolkit';
import {
	useAnalyticsEvents,
	FireScreenAnalytics,
	fireUIAnalytics,
	ContextualAnalyticsData,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import { useIntl } from '@atlassian/jira-intl';

import messages from '../../messages.tsx';
import { DATA_PRIVACY_LINK } from '../../constants.tsx';

type triggerProps = {
	triggerRef: React.Ref<any>;
	[key: string]: any;
};

type AdControlsProps = {
	isCta: boolean;
	onDismissAd: () => Promise<void>;
	children?: ReactNode;
};

const AdControlsReasonsModal = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const clickedLinkBaseEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'link',
	});

	const onClickPrivacyPolicy = useCallback(() => {
		fireUIAnalytics(clickedLinkBaseEvent, 'adControlsPrivacyPolicy');
	}, [clickedLinkBaseEvent]);

	return (
		<ContextualAnalyticsData sourceName="adControlsReasons" sourceType={MODAL}>
			{formatMessage(messages.reasonsModalExplanation, {
				strong: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
			})}
			<ul css={listStyle}>
				<li>{formatMessage(messages.reasonsModalBulletOne)}</li>
				<li>{formatMessage(messages.reasonsModalBulletTwo)}</li>
			</ul>
			<Link href={DATA_PRIVACY_LINK} target="_blank" onClick={onClickPrivacyPolicy}>
				{formatMessage(messages.privacyPolicy)}
			</Link>
			<FireScreenAnalytics attributes={{ experiment: 'backlogButtonRefresh' }} />
		</ContextualAnalyticsData>
	);
};

export const AdControls = ({ isCta, onDismissAd, children }: AdControlsProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const ctaIcon = (iconProps: NewCoreIconProps) => (
		<ShowMoreHorizontalIcon {...iconProps} color={token('color.text.inverse')} />
	);

	const bannerIcon = (iconProps: NewCoreIconProps) => (
		<ShowMoreHorizontalIcon {...iconProps} color={token('color.icon')} />
	);

	const moreIcon = isCta ? ctaIcon : bannerIcon;

	const onClickDropdown = useCallback(
		(
			onClick: (data: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void,
			data: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		) => {
			onClick(data);
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				'adControlsDropdown',
				{
					experiment: 'backlogButtonRefresh',
				},
			);
		},
		[createAnalyticsEvent],
	);

	const trigger = ({ triggerRef, ...props }: triggerProps) => (
		<IconButton
			{...props}
			// eslint-disable-next-line jira/react-no-inline-function-prop
			onClick={(data) => onClickDropdown(props.onClick, data)}
			ref={triggerRef}
			icon={moreIcon}
			label={formatMessage(messages.adControls)}
			isSelected={false}
			appearance="subtle"
		/>
	);

	return (
		<Flex alignItems="center" justifyContent="end" gap="space.100">
			<AdControlDropdownMenu
				itemId="placeholder-template-ad-controls"
				trigger={trigger}
				zIndex={1000}
				placement="bottom-start"
			>
				{(props) => (
					<>
						<DismissalButton
							{...props}
							flagProps={{
								title: formatMessage(messages.dismissFlagTitle),
								description: formatMessage(messages.dismissFlagDescription),
							}}
							onClick={onDismissAd}
						/>
						<ReasonsButton
							{...props}
							modalProps={{
								title: <Heading size="medium">{formatMessage(messages.reasonsModalTitle)}</Heading>,
								body: <AdControlsReasonsModal />,
							}}
						/>
					</>
				)}
			</AdControlDropdownMenu>
			{children}
		</Flex>
	);
};

const listStyle = css({
	marginBottom: token('space.150'),
});
